/**
 * The projects in the active organisation.
 *
 * Keyed on `orgId` with the token read from a ref at fetch time — the same shape
 * as `useOrganizations` and `useUserPermissions`, and for the same reason: the
 * single-session guard rotates the access token on focus, visibility and every
 * realtime reconnect, and keying on it would refetch the list each time for no
 * new information.
 *
 * The states are distinct on purpose. "No projects" and "could not load" look
 * identical if both render an empty list, and they are the two things a user
 * needs told apart (item AO's Verify).
 *
 * `idle` means "there is genuinely no organisation to list projects for" — never
 * "we do not know yet". Until the organisation list settles there is no `orgId`,
 * and reporting that as `idle` flashed "No organization selected." on the way in
 * to every session. `orgsPending` keeps that window in `loading`.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL, isConfigured } from "../lib/config";
import { getProjects, type Project } from "../lib/projects";
import { OrganizationsError } from "../lib/organizations";

export type ProjectsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; projects: Project[] };

export function useProjects(
  orgId: string | null,
  token: string | null,
  /** The organisation list has not settled yet, so a null `orgId` means "not known". */
  orgsPending = false,
): ProjectsState & { reload: () => void } {
  const [state, setState] = useState<ProjectsState>({ kind: "idle" });
  /** Bumped by `reload`, so a failure is recoverable without reloading the app. */
  const [attempt, setAttempt] = useState(0);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!orgId || !isConfigured()) {
      // Waiting on the organisation list is a load, not an answer.
      setState(orgsPending && isConfigured() ? { kind: "loading" } : { kind: "idle" });
      return;
    }

    let cancelled = false;
    setState({ kind: "loading" });

    getProjects(tokenRef.current, orgId, { apiBaseUrl: API_BASE_URL() })
      .then((projects) => {
        if (!cancelled) setState({ kind: "loaded", projects });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          kind: "error",
          message:
            error instanceof OrganizationsError ? error.message : `could not load: ${String(error)}`,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [orgId, attempt, orgsPending]);

  return { ...state, reload };
}
